import React, { useEffect, useRef, useState } from 'react';
import { Box, Card, CardContent, Divider } from '@mui/material';
import SystemSettingsToggle from './SystemSettingsToggle';
import DeviceAccessControl from './DeviceAccessControl';
import DeviceOwnerSettings from './DeviceOwnerSettings';
import DeviceOwnershipResolutionDialog from './DeviceOwnershipResolutionDialog';

const DeviceSettings = ({
    // System settings props
    initialSystemValue = false,
    initialOwnerId = '',
    ownerOptions = [],
    
    // Access control props
    deviceId,
    isOwner = false,
    isAdmin = false,
    currentUserId,
    initialAccessUsers = [],
    initialRestrictedAccess = false,
    
    // API URLs
    availableUsersUrl,
    subAccountsUrl,
    accessUsersUrl,
    ownershipImpactUrl,
    updateAccessUrl,
    authToken,
    
    // Common props
    disabled = false
}) => {
    const [isSystemDevice, setIsSystemDevice] = useState(initialSystemValue);
    const [selectedOwnerId, setSelectedOwnerId] = useState(initialOwnerId ? String(initialOwnerId) : '');
    const [impactDialogOpen, setImpactDialogOpen] = useState(false);
    const [impactPreview, setImpactPreview] = useState(null);
    const [impactError, setImpactError] = useState('');
    const [resolutionError, setResolutionError] = useState('');
    const [resolutions, setResolutions] = useState({});
    const submitBypassRef = useRef(false);

    const handleSystemChange = (newValue) => {
        setIsSystemDevice(newValue);
    };

    const handleOwnerChange = (newValue) => {
        setSelectedOwnerId(newValue);
    };

    useEffect(() => {
        const hiddenInput = document.querySelector('input[name="ownership_resolution"]');
        if (hiddenInput) {
            hiddenInput.value = '';
        }
    }, [selectedOwnerId, isSystemDevice]);

    useEffect(() => {
        const form = document.getElementById('device-edit-form');
        if (!form || !(isAdmin || isOwner) || !ownershipImpactUrl) {
            return undefined;
        }

        /**
         * Reads the current hidden form values that define future device access state.
         */
        const collectFormState = () => {
            const restrictedInput = document.querySelector('input[name="restricted_access"]');
            const usersInput = document.querySelector('input[name="device_access_users"]');
            const ownerInput = document.querySelector('input[name="id_client"]');
            const parsedUsers = usersInput?.value ? JSON.parse(usersInput.value || '[]') : [];

            return {
                system: isSystemDevice,
                restricted_access: restrictedInput?.value === '1',
                device_access_users: Array.isArray(parsedUsers) ? parsedUsers : [],
                id_client: ownerInput?.value || selectedOwnerId || initialOwnerId,
            };
        };

        /**
         * Calls the preview endpoint before save to determine whether flux resolutions are required.
         */
        const previewImpact = async (payload) => {
            const csrfToken = document.querySelector('input[name="_token"]')?.value ||
                document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

            const headers = {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrfToken,
                'X-Requested-With': 'XMLHttpRequest'
            };

            if (authToken) {
                headers.Authorization = `Bearer ${authToken}`;
            }

            const response = await fetch(ownershipImpactUrl, {
                method: 'POST',
                headers,
                credentials: 'same-origin',
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`Failed to preview ownership impact: ${response.status}`);
            }

            return response.json();
        };

        /**
         * Intercepts save to open the resolution dialog only when impacted owners are detected.
         */
        const handleSubmit = async (event) => {
            if (submitBypassRef.current) {
                return;
            }

            event.preventDefault();
            setImpactError('');
            setResolutionError('');

            try {
                const preview = await previewImpact(collectFormState());
                const impactedOwners = Array.isArray(preview?.impacted_owners) ? preview.impacted_owners : [];

                if (impactedOwners.length === 0) {
                    const ownershipResolutionInput = document.querySelector('input[name="ownership_resolution"]');
                    if (ownershipResolutionInput) {
                        ownershipResolutionInput.value = '';
                    }

                    submitBypassRef.current = true;
                    form.submit();
                    return;
                }

                const nextResolutions = impactedOwners.reduce((carry, entry) => {
                    const ownerId = entry?.user?.id;
                    if (!ownerId) {
                        return carry;
                    }

                    carry[ownerId] = resolutions[ownerId] || { action: '', target_device_id: '' };
                    return carry;
                }, {});

                setResolutions(nextResolutions);
                setImpactPreview(preview);
                setImpactDialogOpen(true);
            } catch (error) {
                console.error(error);
                setImpactError('Could not validate the device ownership impact. Fix the issue or try again.');
            }
        };

        form.addEventListener('submit', handleSubmit);

        return () => {
            form.removeEventListener('submit', handleSubmit);
        };
    }, [authToken, initialOwnerId, isAdmin, isOwner, isSystemDevice, ownershipImpactUrl, resolutions, selectedOwnerId]);

    /**
     * Updates the chosen resolution for one impacted owner.
     */
    const handleResolutionChange = (ownerId, value) => {
        setResolutionError('');
        setResolutions((current) => ({
            ...current,
            [ownerId]: value,
        }));
    };

    /**
     * Validates and commits the modal selection back into the hidden form payload.
     */
    const handleDialogConfirm = () => {
        const impactedOwners = impactPreview?.impacted_owners || [];

        for (const entry of impactedOwners) {
            const ownerId = entry?.user?.id;
            const resolution = resolutions[ownerId];

            if (!resolution?.action) {
                setResolutionError(`Choose a resolution for ${entry?.user?.name || 'each impacted account'}.`);
                return;
            }

            if (resolution.action === 'move_to_device' && !resolution.target_device_id) {
                setResolutionError(`Select a target device for ${entry?.user?.name || 'the impacted account'}.`);
                return;
            }
        }

        const hiddenInput = document.querySelector('input[name="ownership_resolution"]');
        if (hiddenInput) {
            hiddenInput.value = JSON.stringify(resolutions);
            hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
            hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const form = document.getElementById('device-edit-form');
        setImpactDialogOpen(false);
        submitBypassRef.current = true;
        form?.submit();
    };

    // Show access control only if:
    // 1. It's not a system device (system devices are available to all)
    // 2. User is admin or owner
    const showAccessControl = !isSystemDevice && (isAdmin || isOwner);

    return (
        <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
                <DeviceOwnerSettings
                    isAdmin={isAdmin}
                    disabled={disabled}
                    initialOwnerId={initialOwnerId}
                    ownerOptions={ownerOptions}
                    onOwnerChange={handleOwnerChange}
                />

                {isAdmin && <Divider sx={{ my: 2 }} />}

                {/* System Settings Toggle - Admin only */}
                <SystemSettingsToggle
                    initialValue={initialSystemValue}
                    disabled={disabled}
                    isAdmin={isAdmin}
                    onSystemChange={handleSystemChange}
                />

                {/* Divider if both sections are shown */}
                {isAdmin && showAccessControl && (
                    <Divider sx={{ my: 2 }} />
                )}

                {/* Device Access Control - Admin and Owners (hidden when system device) */}
                {showAccessControl && (
                    <DeviceAccessControl
                        deviceId={deviceId}
                        isOwner={isOwner}
                        isAdmin={isAdmin}
                        currentUserId={currentUserId}
                        initialAccessUsers={initialAccessUsers}
                        initialRestrictedAccess={initialRestrictedAccess}
                        disabled={disabled}
                        availableUsersUrl={availableUsersUrl}
                        subAccountsUrl={subAccountsUrl}
                        accessUsersUrl={accessUsersUrl}
                        updateAccessUrl={updateAccessUrl}
                        authToken={authToken}
                    />
                )}

                <DeviceOwnershipResolutionDialog
                    open={impactDialogOpen}
                    impactedOwners={impactPreview?.impacted_owners || []}
                    futureOwner={impactPreview?.future_owner || null}
                    deleteAllowed={Boolean(impactPreview?.delete_allowed)}
                    resolutions={resolutions}
                    onResolutionChange={handleResolutionChange}
                    onClose={() => setImpactDialogOpen(false)}
                    onConfirm={handleDialogConfirm}
                    error={impactError || resolutionError}
                />
            </CardContent>
        </Card>
    );
};

export default DeviceSettings;
